import { useEffect, useState } from "react";
import {
    Plus,
    X,
    Edit3,
    Trash2,
    Search,
    ChevronLeft,
    ChevronRight,
    MapPin,
    CheckCircle,
    XCircle,
} from "lucide-react";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export default function LocationPage({ setCurrentPage }) {
    const [locations, setLocations] = useState([]);
    const [filtered, setFiltered] = useState([]);

    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);

    const [locationCode, setLocationCode] = useState("");
    const [locationName, setLocationName] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [search, setSearch] = useState("");

    // Pagination
    const [page, setPage] = useState(1);
    const perPage = 8;
    const totalPages = Math.ceil(filtered.length / perPage);
    const paginated = filtered.slice((page - 1) * perPage, page * perPage);

    // ---------------- API FUNCTIONS ---------------- //

    const fetchLocations = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/Location`);
            if (!res.ok) throw new Error("Failed to fetch locations");

            const data = await res.json();

            // Map to handle both camelCase and PascalCase
            const mapped = data.map((l) => ({
                id: l.id,
                locationCode: l.locationCode || l.LocationCode,
                locationName: l.locationName || l.LocationName,
                isActive: l.isActive ?? l.IsActive ?? true,
            }));

            setLocations(mapped);
            setFiltered(mapped);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to fetch locations", "error");
        }
    };

    const createLocation = async () => {
        if (!locationCode || !locationName) {
            Swal.fire("Oops!", "Please fill in required fields", "warning");
            return;
        }

        const body = {
            LocationCode: locationCode,
            LocationName: locationName,
            IsActive: isActive,
        };

        try {
            const res = await fetch(`${API_BASE_URL}api/Location`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!res.ok) throw new Error("Failed to create location");

            setOpenCreate(false);
            clearForm();
            fetchLocations();
            Swal.fire("Success!", "Location created successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    const updateLocation = async () => {
        if (!locationCode || !locationName) {
            Swal.fire("Oops!", "Please fill in required fields", "warning");
            return;
        }

        const body = {
            LocationCode: locationCode,
            LocationName: locationName,
            IsActive: isActive,
        };

        try {
            const res = await fetch(
                `${API_BASE_URL}api/Location/${selectedLocation.id}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                }
            );

            if (!res.ok) throw new Error("Failed to update location");

            setOpenEdit(false);
            setSelectedLocation(null);
            clearForm();
            fetchLocations();
            Swal.fire("Updated!", "Location updated successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    const deleteLocation = async (location) => {
        const result = await Swal.fire({
            title: `Are you sure?`,
            text: `You are about to delete ${location.locationName}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                const res = await fetch(
                    `${API_BASE_URL}api/Location/${location.id}`,
                    { method: "DELETE" }
                );
                if (!res.ok) throw new Error("Failed to delete location");
                fetchLocations();
                Swal.fire("Deleted!", "Location has been deleted.", "success");
            } catch (err) {
                console.error(err);
                Swal.fire("Error", err.message, "error");
            }
        }
    };

    // ---------------- USE EFFECTS ---------------- //

    useEffect(() => {
        fetchLocations();
    }, []);

    useEffect(() => {
        if (!search.trim()) {
            setFiltered(locations);
        } else {
            const term = search.toLowerCase();
            setFiltered(
                locations.filter(
                    (l) =>
                        l.locationName?.toLowerCase().includes(term) ||
                        l.locationCode?.toLowerCase().includes(term)
                )
            );
        }
        setPage(1);
    }, [search, locations]);

    // ---------------- HELPERS ---------------- //

    const clearForm = () => {
        setLocationCode("");
        setLocationName("");
        setIsActive(true);
    };

    const openEditModal = (l) => {
        setSelectedLocation(l);
        setLocationCode(l.locationCode);
        setLocationName(l.locationName);
        setIsActive(l.isActive);
        setOpenEdit(true);
    };

    // ---------------- RETURN UI ---------------- //
    return (
        <div className="min-h-screen from-gray-50 to-gray-100 p-8">
            {/* BACK BUTTON */}
            <button
                onClick={() => setCurrentPage("referencess")}
                className="mb-2 flex items-center gap-5 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors group ml-8"
            >
                <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                Back to References
            </button>

            {/* HEADER */}
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                            <MapPin className="text-indigo-600" size={36} />
                            Locations
                        </h1>
                        <p className="text-gray-600 mt-2">Manage your business locations</p>
                    </div>
                    <button
                        onClick={() => setOpenCreate(true)}
                        className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    >
                        <Plus size={20} /> Create Location
                    </button>
                </div>
            </div>

            {/* SEARCH BAR */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by location name or code..."
                        className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                                <th className="px-6 py-4 text-left text-sm font-semibold">ID</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Location Code</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Location Name</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Status</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {paginated.length > 0 ? (
                                paginated.map((loc, index) => (
                                    <tr
                                        key={loc.id || `loc-${index}`}
                                        className="hover:bg-indigo-50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 text-indigo-800 font-semibold text-sm">
                                                {loc.id}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                {loc.locationCode}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                                                    <MapPin size={20} />
                                                </div>
                                                <span className="font-semibold text-gray-800">
                                                    {loc.locationName}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center">
                                                {loc.isActive ? (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <CheckCircle size={14} />
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        <XCircle size={14} />
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openEditModal(loc)}
                                                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Edit"
                                                >
                                                    <Edit3 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => deleteLocation(loc)}
                                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3 text-gray-400">
                                            <MapPin size={48} />
                                            <p className="text-lg font-medium">No locations found</p>
                                            <p className="text-sm">
                                                Try adjusting your search or create a new location
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {paginated.length > 0 && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                                Showing {(page - 1) * perPage + 1} to{" "}
                                {Math.min(page * perPage, filtered.length)} of {filtered.length}{" "}
                                locations
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="text-sm font-semibold text-gray-700">
                                    Page {page} of {totalPages || 1}
                                </span>
                                <button
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL */}
            {(openCreate || openEdit) && (
                <Modal
                    title={openCreate ? "Create New Location" : "Edit Location"}
                    close={() => {
                        setOpenCreate(false);
                        setOpenEdit(false);
                        setSelectedLocation(null);
                        clearForm();
                    }}
                >
                    <div className="space-y-4">
                        <Input
                            label="Location Code*"
                            value={locationCode}
                            onChange={setLocationCode}
                            required
                        />
                        <Input
                            label="Location Name*"
                            value={locationName}
                            onChange={setLocationName}
                            required
                        />
                        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                id="isActive"
                                className="h-5 w-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                            />
                            <label htmlFor="isActive" className="text-sm font-semibold text-gray-700 cursor-pointer">
                                Active Location
                            </label>
                        </div>
                    </div>

                    <button
                        onClick={openCreate ? createLocation : updateLocation}
                        className="mt-6 w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                    >
                        {openCreate ? "Create Location" : "Update Location"}
                    </button>
                </Modal>
            )}
        </div>
    );
}

// Modal Component
function Modal({ title, close, children }) {
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={close}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition"
                >
                    <X size={24} />
                </button>
                <h2 className="text-3xl font-bold text-gray-800 mb-8">{title}</h2>
                {children}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required = false }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
            />
        </div>
    );
}